/**
 * Lightweight wrapper that provides the edit-mode context, sidebar, and
 * toolbar to any subtree.  PageLayout uses this internally for normal pages;
 * gate pages (coming-soon, under-construction) and the error fallback can
 * use it directly so that the on-site CMS is still accessible.
 */
import { lazy, Suspense } from "react";
import { Link } from "react-router";
import { isEditorEnabled } from "@/lib/editorMode";

// Lazy editor imports — tree-shaken when editing is disabled
const EditModeProviderLazy = isEditorEnabled
  ? lazy(() =>
      import("@/contexts/EditModeContext").then((m) => ({
        default: m.EditModeProvider,
      })),
    )
  : null;

const EditSidebarLazy = isEditorEnabled
  ? lazy(() => import("@/components/dev/EditSidebar"))
  : null;

const EditToggleLazy = isEditorEnabled
  ? lazy(() => import("@/components/dev/EditToggle"))
  : null;

const AddRetreatButtonLazy = isEditorEnabled
  ? lazy(() => import("@/components/dev/AddRetreatButton"))
  : null;

export function DevToolbar() {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 9999,
        display: "flex",
        gap: 8,
        alignItems: "center",
      }}
    >
      {AddRetreatButtonLazy && (
        <Suspense fallback={null}>
          <AddRetreatButtonLazy />
        </Suspense>
      )}
      {EditToggleLazy && (
        <Suspense fallback={null}>
          <EditToggleLazy />
        </Suspense>
      )}
      {import.meta.env.DEV && (
        <Link
          to="/dev"
          aria-label="Dev Hub"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            backgroundColor: "#1e1e1e",
            color: "#999",
            fontSize: 18,
            borderRadius: "50%",
            textDecoration: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            opacity: 0.5,
            transition: "opacity 150ms, color 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1";
            e.currentTarget.style.color = "#e5e5e5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.5";
            e.currentTarget.style.color = "#999";
          }}
        >
          &#9881;
        </Link>
      )}
    </div>
  );
}

interface EditorShellProps {
  children: React.ReactNode;
}

export default function EditorShell({ children }: EditorShellProps) {
  if (!isEditorEnabled || !EditModeProviderLazy) {
    return <>{children}</>;
  }

  return (
    <Suspense fallback={null}>
      <EditModeProviderLazy>
        {children}
        <DevToolbar />
        {EditSidebarLazy && (
          <Suspense fallback={null}>
            <EditSidebarLazy />
          </Suspense>
        )}
      </EditModeProviderLazy>
    </Suspense>
  );
}
